import { alpha, Box, Group, lighten, rem, Stack, Text } from '@mantine/core'
import { memo, type CSSProperties, useCallback, useRef, useState, type RefObject } from 'react'
import type { BogglePlaygroundMeterSettings } from '~/boggle/config/playground'
import {
  getBoggleMeterDisplayedChargeRatio,
  getBoggleMeterOvermeterGlowEfficacy,
  getBoggleMeterTileSpawnIntervalMs,
} from '~/boggle/lib/meter'
import { useBoggleImperativeTimePaint } from '~/boggle/ui/run/useBoggleImperativeTimePaint'
import type { BoggleStore } from '~/boggle/ui/state/Store'
import styles from './MeterGauge.module.css'

const ARC_FRACTION = 0.75

/** normalized total meter gain (main + overmeter, same units as `applyBoggleMeterDelta`) at which pulse hits full intensity */
const METER_GAIN_PULSE_CAP_FRAC = 0.2

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value))
}

function resolveMeterProgressColor(
  burstActive: boolean,
  tileSpawnGlowActive: boolean,
  value: number,
) {
  if (burstActive) {
    return 'rgb(200, 110, 20)'
  }
  if (tileSpawnGlowActive) {
    return '#ffb256'
  }
  if (value <= 0) {
    return 'rgba(242, 232, 217, 0.35)'
  }
  return '#f0c85a'
}

function paintMeterGaugeArc({
  capacity,
  filterBoxRef,
  burstActive,
  tileSpawnGlowActive,
  overmeterValue,
  progressCircleRef,
  progressRootRef,
  ringCircumference,
  settings,
  value,
  valueTextRef,
}: {
  capacity: number
  filterBoxRef: RefObject<HTMLDivElement | null>
  burstActive: boolean
  tileSpawnGlowActive: boolean
  overmeterValue: number
  progressCircleRef: RefObject<SVGCircleElement | null>
  progressRootRef: RefObject<HTMLDivElement | null>
  ringCircumference: number
  settings: BogglePlaygroundMeterSettings
  value: number
  valueTextRef: RefObject<HTMLSpanElement | null>
}) {
  const displayChargeRatio =
    capacity <= 0
      ? 0
      : burstActive
        ? clamp(value / capacity, 0, 1)
        : getBoggleMeterDisplayedChargeRatio({ value, overmeterValue }, capacity)
  const normalizedProgress = capacity <= 0 ? 0 : clamp(displayChargeRatio, 0, 1)
  const arcLength = ARC_FRACTION * ringCircumference
  const filledArcLength = normalizedProgress * arcLength
  const progressDashPattern = `${filledArcLength} ${ringCircumference}`
  const resolvedProgressColor = resolveMeterProgressColor(
    burstActive,
    tileSpawnGlowActive,
    value,
  )

  const progressCircle = progressCircleRef.current
  if (progressCircle) {
    progressCircle.style.stroke = resolvedProgressColor
    progressCircle.style.strokeDasharray = progressDashPattern
    progressCircle.style.opacity = normalizedProgress > 0 ? '1' : '0'
  }

  const valueText = valueTextRef.current
  if (valueText && !burstActive) {
    valueText.textContent = String(Math.round(Math.max(0, displayChargeRatio * 100)))
  }

  const progressRoot = progressRootRef.current
  if (progressRoot) {
    progressRoot.setAttribute('aria-valuenow', String(Math.round(displayChargeRatio * 100)))
  }

  const bonusSpillGlow = clamp(
    getBoggleMeterOvermeterGlowEfficacy(overmeterValue, capacity),
    0,
    1,
  )
  const showOvermeterGlow =
    settings.tileSpawn?.enabled && !burstActive && value >= capacity
  const staticOvermeterFilter =
    showOvermeterGlow && !tileSpawnGlowActive
      ? `drop-shadow(0 0 ${5 + bonusSpillGlow * 7}px rgba(255, 251, 232, ${0.35 + bonusSpillGlow * 0.35}))`
      : undefined
  const filterBox = filterBoxRef.current
  if (filterBox) {
    filterBox.style.filter = staticOvermeterFilter ?? ''
  }
}

export const MeterGauge = memo(function MeterGauge({
  fillGainPulseEnabled = true,
  tileSpawnGlowActive = false,
  burstActive = false,
  diameter: layoutDiameter = 120,
  fontMetricsDiameter,
  marginTop = 6,
  overmeterValue = 0,
  progressMeterWidth = 12,
  settings,
  store,
  value = 0,
}: {
  fillGainPulseEnabled?: boolean
  tileSpawnGlowActive?: boolean
  burstActive?: boolean
  diameter?: number
  fontMetricsDiameter?: number
  marginTop?: number
  overmeterValue?: number
  progressMeterWidth?: number
  settings: BogglePlaygroundMeterSettings
  /** Live clock — arc and center text update imperatively on `subscribeTime`. */
  store?: BoggleStore
  /** Static snapshot when `store` is omitted (e.g. end-buffer HUD). */
  value?: number
}) {
  const pulseIdRef = useRef(0)
  const prevCombinedChargeRatioRef = useRef(0)
  const hasPaintedRef = useRef(false)
  const progressCircleRef = useRef<SVGCircleElement>(null)
  const progressRootRef = useRef<HTMLDivElement>(null)
  const filterBoxRef = useRef<HTMLDivElement>(null)
  const valueTextRef = useRef<HTMLSpanElement>(null)
  const [meterGainPulse, setMeterGainPulse] = useState<{
    durationMs: number
    id: number
    strength: number
  } | null>(null)

  const drawDiameter = burstActive ? layoutDiameter * 1.2 : layoutDiameter
  const ringStrokeWidth = burstActive
    ? progressMeterWidth + Math.max(4, Math.round(progressMeterWidth * 0.8))
    : progressMeterWidth
  const padding = Math.max(12, ringStrokeWidth / 2 + 6)
  const radius = drawDiameter / 2 - padding
  const ringCircumference = 2 * Math.PI * radius
  const arcLength = ARC_FRACTION * ringCircumference
  const trackDashPattern = `${arcLength} ${ringCircumference - arcLength}`
  const center = drawDiameter / 2
  const fontBasisDiameter = fontMetricsDiameter ?? layoutDiameter
  const valueFontSize = Math.max(10, Math.round(fontBasisDiameter * 0.15))
  const showCenterValue = !burstActive
  const conversionIntervalMs =
    getBoggleMeterTileSpawnIntervalMs({ value, overmeterValue }, settings) ?? 3_000
  const pulsePeriodMs = Math.max(250, conversionIntervalMs)
  const resolvedProgressColor = resolveMeterProgressColor(
    burstActive,
    tileSpawnGlowActive,
    value,
  )
  const staticDisplayChargeRatio =
    store == null
      ? settings.capacity <= 0
        ? 0
        : burstActive
          ? clamp(value / settings.capacity, 0, 1)
          : getBoggleMeterDisplayedChargeRatio({ value, overmeterValue }, settings.capacity)
      : null
  const staticNormalizedProgress =
    staticDisplayChargeRatio !== null ? clamp(staticDisplayChargeRatio, 0, 1) : 0
  const staticFilledArcLength = staticNormalizedProgress * arcLength
  const staticProgressDashPattern =
    staticDisplayChargeRatio !== null
      ? `${staticFilledArcLength} ${ringCircumference}`
      : `0 ${ringCircumference}`

  const paint = useCallback(() => {
    const meter = store?.getTimeState().meter
    const liveValue = meter?.value ?? value
    const liveOvermeter = meter?.overmeterValue ?? overmeterValue
    const capacity = settings.capacity

    const displayChargeRatio =
      capacity <= 0
        ? 0
        : burstActive
          ? clamp(liveValue / capacity, 0, 1)
          : getBoggleMeterDisplayedChargeRatio(
              { value: liveValue, overmeterValue: liveOvermeter },
              capacity,
            )

    if (fillGainPulseEnabled && hasPaintedRef.current) {
      const dChargeRatio = Math.max(
        0,
        displayChargeRatio - prevCombinedChargeRatioRef.current,
      )
      const strength =
        capacity > 0 && dChargeRatio > 0
          ? clamp(dChargeRatio / METER_GAIN_PULSE_CAP_FRAC, 0, 1)
          : 0

      if (strength > 0) {
        pulseIdRef.current += 1
        const durationMs = Math.round(500 + strength * 500)
        setMeterGainPulse({
          id: pulseIdRef.current,
          durationMs,
          strength,
        })
      }
    }

    prevCombinedChargeRatioRef.current = displayChargeRatio
    hasPaintedRef.current = true

    paintMeterGaugeArc({
      capacity,
      filterBoxRef,
      burstActive,
      tileSpawnGlowActive,
      overmeterValue: liveOvermeter,
      progressCircleRef,
      progressRootRef,
      ringCircumference,
      settings,
      value: liveValue,
      valueTextRef,
    })
  }, [
    fillGainPulseEnabled,
    tileSpawnGlowActive,
    burstActive,
    overmeterValue,
    ringCircumference,
    settings,
    store,
    value,
  ])

  useBoggleImperativeTimePaint(
    paint,
    [
      fillGainPulseEnabled,
      tileSpawnGlowActive,
      burstActive,
      overmeterValue,
      ringCircumference,
      settings,
      value,
    ],
    store ?? null,
  )

  return (
    <Stack
      ref={progressRootRef}
      align="center"
      aria-valuemin={0}
      aria-valuenow={
        staticDisplayChargeRatio !== null
          ? Math.round(staticDisplayChargeRatio * 100)
          : undefined
      }
      gap={0}
      h={layoutDiameter}
      justify="center"
      miw={layoutDiameter}
      mt={marginTop}
      pos="relative"
      role="progressbar"
      w={layoutDiameter}
    >
      <Box
        h={layoutDiameter}
        pos="relative"
        style={{ overflow: 'visible' }}
        w={layoutDiameter}
      >
        <Box
          h={drawDiameter}
          left="50%"
          pos="absolute"
          style={{
            ...(tileSpawnGlowActive
              ? ({
                  '--meter-gold-ms': `${pulsePeriodMs}ms`,
                } as CSSProperties)
              : null),
            transform: 'translate(-50%, -50%)',
          }}
          top="50%"
          w={drawDiameter}
        >
          {tileSpawnGlowActive ? (
            <Box
              className={styles.fullPulse}
              pos="absolute"
              top="50%"
              left="50%"
              h={0}
              w={0}
            />
          ) : null}
          {fillGainPulseEnabled && meterGainPulse ? (
            <Box
              key={meterGainPulse.id}
              className={styles.gainCore}
              data-gain-pulse-id={meterGainPulse.id}
              onAnimationEnd={event => {
                const endedId = (event.currentTarget as HTMLElement | SVGElement).dataset
                  .gainPulseId
                setMeterGainPulse(current =>
                  current !== null && String(current.id) === endedId ? null : current,
                )
              }}
              pos="absolute"
              style={{
                '--meter-gain-duration': `${meterGainPulse.durationMs}ms`,
                '--meter-gain-strength': String(meterGainPulse.strength),
                '--meter-gain-color': alpha(
                  lighten(resolvedProgressColor, 0.2),
                  0.4 + 0.6 * meterGainPulse.strength,
                ),
              }}
            />
          ) : null}
          <Box
            ref={filterBoxRef}
            h={drawDiameter}
            pos="relative"
            style={{
              position: 'relative',
              zIndex: 1,
            }}
            w={drawDiameter}
          >
            <Box
              h={drawDiameter}
              pos="relative"
              style={{ flexShrink: 0, overflow: 'visible' }}
              w={drawDiameter}
            >
              <svg
                className={styles.ring}
                height="100%"
                style={{ display: 'block' }}
                viewBox={`0 0 ${drawDiameter} ${drawDiameter}`}
                width="100%"
              >
                <circle
                  className={styles.track}
                  cx={center}
                  cy={center}
                  r={radius}
                  style={{
                    strokeDasharray: trackDashPattern,
                    strokeWidth: ringStrokeWidth,
                    stroke: 'rgba(0, 0, 0, 0.7)',
                  }}
                />
                <circle
                  ref={progressCircleRef}
                  className={styles.progress}
                  cx={center}
                  cy={center}
                  r={radius}
                  style={{
                    stroke: resolvedProgressColor,
                    strokeDasharray: staticProgressDashPattern,
                    strokeDashoffset: 0,
                    strokeWidth: ringStrokeWidth,
                    opacity: staticNormalizedProgress > 0 ? 1 : 0,
                  }}
                />
              </svg>
            </Box>
            {showCenterValue ? (
              <Stack
                align="center"
                bottom={0}
                gap={2}
                justify="center"
                left={0}
                pos="absolute"
                right={0}
                top={0}
                style={{
                  textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
                }}
              >
                <Group
                  pos="relative"
                  justify="center"
                  align="center"
                  gap={2}
                  wrap="nowrap"
                  left={6}
                >
                  <Text
                    fz={valueFontSize}
                    fw={500}
                    lh={1}
                    style={{
                      height: rem(valueFontSize),
                      transformOrigin: '100% 50%',
                    }}
                  >
                    <span ref={valueTextRef}>
                      {staticDisplayChargeRatio !== null
                        ? Math.round(Math.max(0, staticDisplayChargeRatio * 100))
                        : 0}
                    </span>
                  </Text>
                  <Text fz={valueFontSize - 2} opacity={0.7}>
                    %
                  </Text>
                </Group>
              </Stack>
            ) : null}
          </Box>
        </Box>
      </Box>
    </Stack>
  )
})
